<template>
  <div>
    <SoDataTableServer
      ref="dataTable"
      v-model:options="syncedAccountsStore.tableOptions"
      :headers="tableHeaders"
      :data="tableData"
      :auto-search="false"
      data-testid="admin-sync-accounts-table"
      searchable
      has-filters
      @fetch="fetchData"
    >
      <template #beforeSearch>
        <SoSelect
          v-model="syncedAccountsStore.searchArgs.searchTerm"
          :items="searchTerms"
          data-testid="admin-sync-accounts-search-term"
          type="simple"
        />
      </template>

      <template #tableActions>
        <SoToggle
          v-if="!isProdDb"
          v-model="syncedAccountsStore.searchArgs.showSyncedAccounts"
          data-testid="admin-sync-accounts-synced-switch"
          label="Synced Accounts Only"
          class="mx-3 mt-1"
        />

        <SoButton
          icon="refresh"
          class="ml-auto"
          type="secondary"
          data-testid="admin-sync-accounts-refresh-btn"
          large
          @click="refreshData()"
        />
      </template>

      <template #item.customerName="{ internalItem }">
        <div
          v-if="internalItem.raw.customerName"
          :title="internalItem.raw.customerName"
          class="text-ellipsis customer-name-cell"
        >
          {{ internalItem.raw.customerName }}
        </div>
      </template>

      <template #item.coreId="{ internalItem }">
        <span v-if="internalItem.raw.coreId" class="id-cell">
          {{ internalItem.raw.coreId }}
        </span>
        <span v-else class="text-no-wrap text-default id-cell">Not synced</span>
      </template>

      <template #item.users="{ internalItem }">
        <template v-if="internalItem.raw.hasUser">
          <div
            v-for="(user, i) in internalItem.raw.users"
            :key="i"
            class="text-ellipsis email-cell"
          >
            <router-link
              v-if="user"
              :to="`/admin/user/${user.id}`"
              class="text-primary"
              :title="user.email"
            >
              {{ user.email }}
            </router-link>
          </div>
        </template>
        <span
          v-else-if="internalItem.raw.isOccupierAccount"
          class="text-no-wrap text-default"
        >
          No user (occupier acc.)
        </span>
        <span v-else class="text-no-wrap text-default">
          No user
          <template v-if="internalItem.raw.isSynced && !isProdDb">
            |
            <a
              href="javascript:void(0)"
              class="ml-1 text-default"
              @click="createUser(internalItem.raw)"
            >
              Create +
            </a>
          </template>
        </span>
      </template>

      <template #item.isQaAccount="{ internalItem }">
        <div class="d-flex align-items-center justify-center">
          <SoIcon :icon="internalItem.raw.isQaAccount ? 'check' : 'close'" />
        </div>
      </template>

      <template #itemActions="{ item }">
        <div class="d-inline-flex align-center gap-1">
          <SoProgress
            v-if="processedId === item.juniferId"
            :size="24"
            icon="loading"
            class="mr-2 ml-1"
            indeterminate
          />
          <SoMenu
            v-else
            icon="ellipsis"
            button-type="icon"
            :items="getItemActions(item)"
          >
          </SoMenu>
          <SoButton
            :to="`/customer/${item.number}`"
            :disabled="!item.isSynced"
            type="icon"
            icon="chevron-right"
            class="mr-0"
          />
        </div>
      </template>
    </SoDataTableServer>
    <AdminSyncAccountCreateUserModal
      v-if="selectedAccountNumber"
      v-model="isCreateUserModalOpen"
      data-testid="admin-sync-accounts-create-user-modal"
      :account-number="selectedAccountNumber"
      @close="onCreateUserModalClose"
      @success="refreshData()"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoMenu, { type SoMenuItemType } from "@/components/SoMenu.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import { AccountSyncService } from "@/services/admin/AccountSyncService"
  import { RouteNames } from "@/types/router"
  import { UserService } from "@/services/admin/UserService"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { errorReporter } from "@/services/ErrorService"
  import {
    AccountSyncTableFilters,
    SearchTerms,
    syncAccountTableHeaders,
  } from "./syncAccountConfig"
  import AdminSyncAccountCreateUserModal from "@/views/admin/syncAccounts/modals/AdminSyncAccountCreateUserModal.vue"
  import { IAccountSyncResult } from "@/models/admin/accountSync/accountSyncResult/accountSyncResult.types"
  import { useSyncedAccountsStore } from "@/stores/syncedAccounts"
  import SoIcon from "@/components/SoIcon.vue"

  export default defineComponent({
    components: {
      AdminSyncAccountCreateUserModal,
      SoDataTableServer,
      SoMenu,
      SoSelect,
      SoToggle,
      SoProgress,
      SoButton,
      SoIcon,
    },
    data() {
      return {
        RouteNames,
        snackbar: useSnackbarStore(),
        syncedAccountsStore: useSyncedAccountsStore(),
        processedId: null as number | null,
        selectedAccountNumber: null as string | null,
        isCreateUserModalOpen: false,
      }
    },
    computed: {
      query() {
        return AccountSyncService.search(this.syncedAccountsStore.searchArgs)
      },
      tableHeaders() {
        if (!this.isProdDb) return syncAccountTableHeaders
        const hiddenColsOnProd = new Set(["autoSyncStatus", "isQaAccount"])
        return syncAccountTableHeaders.filter(
          (h) => !hiddenColsOnProd.has(h.key),
        )
      },
      tableData(): SoDataTableData {
        const items = this.query.data.items.map((item: IAccountSyncResult) => ({
          ...item,
          hasUser: item.hasUser,
          isOccupierAccount: item.isOccupierAccount,
          isQaAccount: item.isQaAccount,
          status: {
            value: item.status.text,
            props: { status: item.status },
          },
          autoSyncStatus: {
            value: item.autoSyncStatus.text,
            props: { status: item.autoSyncStatus },
          },
        }))

        return {
          isLoading: this.query.isFetching,
          totalItems: this.query.data.total,
          items,
        }
      },
      searchTerms() {
        return Object.entries(SearchTerms).map(([key, value]) => ({
          title: value,
          value: key,
        }))
      },
      isProdDb() {
        return String(import.meta.env.SO_ADMIN_API_URL).includes(".so.energy")
      },
      isDev() {
        return !!import.meta.env.DEV
      },
    },
    created() {
      this.syncedAccountsStore.searchArgs.showSyncedAccounts = !this.isProdDb
    },
    methods: {
      fetchData(tableOptions: SoDataTableOptions<AccountSyncTableFilters>) {
        this.syncedAccountsStore.searchArgs = {
          ...this.syncedAccountsStore.searchArgs,
          ...tableOptions,
        }
      },

      refreshData() {
        if (!this.isProdDb) {
          AccountSyncService.getAutoSyncedAccountsCount().refetch()
        }
        return this.query.refetch()
      },

      getItemActions(item: IAccountSyncResult): SoMenuItemType[] {
        const menuItems: SoMenuItemType[] = []

        if (!item.isAutoSynced && !this.isProdDb) {
          menuItems.push({
            action: () => this.autoSyncAccount(item),
            title: item.isSynced ? "Enable Auto-Sync" : "Synchronise Account",
            icon: "refresh",
          })
        }

        menuItems.push(
          {
            disabled: !item.isSynced && !this.isProdDb,
            action: () => this.openAccountInNova(item),
            title: "Open in Nova",
            icon: "external-link",
          },
          {
            disabled: !item.isSynced || !item.hasUser || this.isDev,
            action: () => this.openInMyAccount(item),
            title: "Open in MyAccount",
            icon: "external-link",
          },
          {
            disabled: !item.isSynced || !item.hasUser,
            action: () => this.copyUserOtp(item),
            title: "Get User OTP",
            icon: "key",
          },
        )

        if (!this.isProdDb) {
          menuItems.push(
            {
              disabled: !item.isSynced,
              action: () => this.resyncAccount(item),
              title: "Resync Account",
              icon: "repeat",
            },
            {
              disabled:
                !!item.hasUser || !item.isSynced || item.isOccupierAccount,
              action: () => this.createUser(item),
              title: "Create User",
              icon: "user-plus",
            },
          )

          if (item.isAutoSynced) {
            menuItems.push({
              disabled: !item.isSynced || item.isAutoSyncLocked,
              action: () => this.disableAutoSync(item),
              title: "Disable Auto-Sync",
              icon: "refresh-off",
            })
          }
        }

        return menuItems
      },

      openAccountInNova(item: IAccountSyncResult) {
        const route = this.$router.resolve({
          name: RouteNames.CustomerOverview,
          params: { accountNumber: item.number },
        })
        window.open(route.href, "_blank")
      },

      async openInMyAccount(item: IAccountSyncResult) {
        try {
          this.processedId = item.juniferId
          if (!item.hasUser) throw new Error("No users found")
          const { id, email } = item.users[0]
          await UserService.signInToMyAccount(id, email)
        } catch (e) {
          this.snackbar.addError(e?.toString() ?? "Failed to generate OTP")
        } finally {
          this.processedId = null
        }
      },

      async copyUserOtp(item: IAccountSyncResult) {
        try {
          this.processedId = item.juniferId
          if (!item.hasUser) throw new Error("No users found")
          await UserService.getOtp(item.users[0].id, true)
          this.snackbar.addSuccess("OTP copied to clipboard")
        } catch (e) {
          errorReporter.report(e)
          console.error(e)
          this.snackbar.addError(e?.toString() ?? "Failed to generate OTP")
        } finally {
          this.processedId = null
        }
      },

      async resyncAccount(item: IAccountSyncResult) {
        try {
          this.processedId = item.juniferId
          await AccountSyncService.resyncAccounts([item.juniferId])
          this.snackbar.addSuccess(
            "Resyncing started and may take up to 5 minutes to complete",
          )
        } catch (e) {
          errorReporter.report(e)
          console.error(e)
          this.snackbar.addError("Failed to sync account")
        } finally {
          this.processedId = null
        }
      },

      async autoSyncAccount(item: IAccountSyncResult) {
        try {
          this.processedId = item.juniferId
          await AccountSyncService.autoSyncAccount(item.number)
          this.snackbar.addSuccess(
            "Auto-Sync enabled, and sync started which may take up to 5 minutes",
          )
        } catch (e) {
          errorReporter.report(e)
          console.error(e)
          this.snackbar.addError("Failed to sync account")
        } finally {
          this.processedId = null
          this.refreshData()
        }
      },

      async disableAutoSync(item: IAccountSyncResult) {
        try {
          this.processedId = item.juniferId
          if (!item.autoSyncId)
            throw new Error("Missing synchronized_accounts item ID")
          await AccountSyncService.disableAutoSync(item.autoSyncId)
          this.snackbar.addSuccess("Auto-Sync disabled")
        } catch (e) {
          errorReporter.report(e)
          console.error(e)
          this.snackbar.addError("Failed to disable auto-sync")
        } finally {
          this.processedId = null
          this.refreshData()
        }
      },

      async createUser(item: IAccountSyncResult) {
        this.processedId = item.juniferId
        this.selectedAccountNumber = item.number
        this.isCreateUserModalOpen = true
      },

      onCreateUserModalClose() {
        this.processedId = null
        this.selectedAccountNumber = null
      },
    },
  })
</script>
<style scoped>
  .email-cell {
    max-width: 150px;
  }
  .customer-name-cell {
    max-width: 130px;
  }
  .id-cell {
    width: 55px;
  }

  :deep(td.v-data-table-column--align-end:last-of-type) {
    padding-right: 0.25rem;
  }
</style>
